import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { css } from 'design'
import { formatTime, getDayName, getMonthName } from 'util/time_formatting'

import { useSanitiseTimelineHtml } from '../../../hooks/timelines/use_sanitise_timeline_html'
import { CalendarIcon } from '../../../images'
import { makeGetMeeting } from '../../../reduxes/slices/meetings/selectors'
import { TimelineType } from '../../../types/models'
import { ResponseOptions } from '../../meetings/response_options'
import { Timestamp } from '../../timekeeping/timestamp'
import Timeline from '../timeline'

type Props = {
  timeline: TimelineType
}

export const Meeting = ({ timeline }: Props) => {
  const getMeeting = useMemo(makeGetMeeting, [])
  const meeting = useSelector((state) => getMeeting(state, timeline?.tl_id))
  const {
    id,
    date: d,
    duration,
    location,
    created_by: createdByHandle,
    status,
    updated_at: updatedAt,
  } = meeting || {}
  const date = useMemo(() => new Date(d), [d])
  const dateFormatted = useMemo(() => {
    return `${getDayName(date)}, ${getMonthName(date)} ${date.getDate()}`
  }, [date])
  const startTime = useMemo(() => {
    return formatTime(date)
  }, [date])
  const endDate = useMemo(() => {
    return new Date(date.getTime() + duration * 60000)
  }, [date, duration])
  const endTime = useMemo(() => {
    return formatTime(endDate)
  }, [endDate])
  const finished = status === 'past' || status === 'cancelled'
  const timestamp =
    finished && (status === 'cancelled' ? updatedAt : endDate.toISOString())
  const locationHTML = useSanitiseTimelineHtml({ html: location })

  if (!id) {
    return null
  }

  return (
    <Timeline
      timeline={timeline}
      item={meeting}
      handle={createdByHandle}
      withActionsBar={false}
      withTimelineInfo={false}
      avatar={
        finished ? (
          <div className="timeline__status__line topic-avatar" />
        ) : (
          <div className={iconContainerStyles()}>
            <i>
              <CalendarIcon />
            </i>
          </div>
        )
      }
      className={finished ? 'timeline__status' : timelineContainerStyles()}
    >
      {!finished && (
        <section className={timelineStyles()}>
          <div className={infoStyles()}>
            <h2>
              <b>{dateFormatted}</b>
              <span
                className={timeStyles()}
              >{`${startTime} - ${endTime}`}</span>
            </h2>
            {location && (
              <div className={'trix-content'}>
                Location:{' '}
                <span dangerouslySetInnerHTML={{ __html: locationHTML }} />
              </div>
            )}
          </div>
          {status === 'future' && (
            <ResponseOptions
              meeting={meeting}
              className={responseOptionsStyles()}
            />
          )}
        </section>
      )}
      {finished && (
        <section>
          {status === 'past' ? 'Meeting ended.' : 'Meeting cancelled.'}
          &nbsp; <b>{dateFormatted}</b>
          &nbsp; <span>{`${startTime} - ${endTime}`}</span>
          {timestamp && (
            <Timestamp
              date={timestamp}
              className="timeline__status__timestamp"
            />
          )}
        </section>
      )}
    </Timeline>
  )
}

const timelineContainerStyles = css({
  '>*:first-child': {
    alignItems: 'center',
  },
})

const timelineStyles = css({
  display: 'flex',
  flexDirection: 'column',
  color: '$grey700',
  border: '1px solid $grey100',
  borderRadius: '$3',
  width: '90%',
  padding: '$2 $3',

  '@md': {
    flexDirection: 'row',
    alignItems: 'center',
  },
})

const infoStyles = css({
  flexGrow: 1,

  h2: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '$3',
    fontWeight: 500,
    color: '$grey700',
  },

  h3: {
    fontSize: '$1',
    color: '$grey500',
    fontWeight: 'normal',
  },

  '@md': {
    h2: {
      flexDirection: 'row',
    },
  },
})

const iconContainerStyles = css({
  minWidth: 42,
  height: 42,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '$grey100',
  borderRadius: '$3',

  path: {
    fill: 'white',
  },
})

const timeStyles = css({
  color: '$grey500',
  fontWeight: 400,

  '@md': {
    marginLeft: '$2',
  },
})

const responseOptionsStyles = css({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '$2',

  button: {
    maxWidth: '150px',
  },

  '@md': {
    marginTop: 0,
  },
})
